/**
 * bcClient.js - Business Central HTTP client.
 *
 * Talks to the backend proxy at /api/proxy/bc/*. The proxy attaches an
 * OAuth2 client-credentials token, so the React app never sees BC
 * credentials directly.
 *
 * The client supports both single-company and cross-company calls:
 *   setCompanyId(null)   -> /api/proxy/bc/companies          (cross-company)
 *   setCompanyId(guid)   -> /api/proxy/bc/companies(guid)/x  (scoped to one)
 *
 * The bearer token is read from the AuthContext (apiAuth.jsx) on every
 * request, so log-out from the React side immediately revokes access.
 */

import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

/* The base BC path on the backend (the tenant/users/schema is encoded
 * into the backend's BC_BASE_URL). All api modules call this client with
 * paths like '/Vendor_Card_Excel'. The base URL is stripped of any
 * tenant suffix so we don't double it up. */
const bcClient = axios.create({
  baseURL: `${API_BASE}/api/proxy/bc`,
  headers: {
    Accept: "application/json",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
  },
  timeout: 30000,
});

/* Module-scoped company ID. Updated by setCompanyId(). */
let currentCompanyId = null;

/**
 * Set (or clear) the company ID for company-scoped requests.
 *
 * @param {string|null} id  - BC company GUID, or null for cross-company calls.
 */
export function setCompanyId(id) {
  currentCompanyId = id || null;
}

/**
 * @returns {string|null} the current company id.
 */
export function getCompanyId() {
  return currentCompanyId;
}

/**
 * Rewrite an endpoint path to scope it to the current company.
 *
 *   /companies              -> /companies                  (cross-company)
 *   /Vendor_Card_Excel      -> companies({id})/vendorCard  (scoped)
 *   /companies({id})/x      -> companies({id})/x           (already scoped)
 */
function rewriteUrl(url, companyId) {
  if (!url) return url;
  if (url.startsWith("/companies(")) return url;
  if (url === "/companies" || url.startsWith("/companies?")) return url;

  if (!companyId) return url;

  // Strip the leading slash; we'll rebuild.
  const tail = url.replace(/^\//, "");
  return `/companies(${companyId})/${tail}`;
}

/* ------------------------------------------------------------------ */
/* Interceptors                                                       */
/* ------------------------------------------------------------------ */

bcClient.interceptors.request.use(
  (config) => {
    // Attach bearer token (if any) from the DB-auth context.
    const tk = getAccessToken && getAccessToken();
    if (tk) {
      config.headers.Authorization = `Bearer ${tk}`;
    }

    // Scope to company if one is selected.
    if (config.url) {
      config.url = rewriteUrl(config.url, currentCompanyId);
    }

    return config;
  },
  (err) => Promise.reject(err)
);

bcClient.interceptors.response.use(
  (response) => {
    // Tag the response so callers can tell which company was used.
    response.__companyId = currentCompanyId;
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      // Extract a useful message regardless of how BC/our proxy
      // structured the body. Common ones we expect:
      //   { error: { message: "..." } }   - BC standard OData error
      //   { error: "string", hint: "..."} - our 503 from the proxy
      //   { message: "..." }              - generic
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.hint ||
        data?.message ||
        (error.message && !/^Request failed/i.test(error.message) ? error.message : null) ||
        "Unknown BC error";
      // Attach the hint (if any) so the UI can show it inline.
      const hint = typeof data?.hint === "string" ? data.hint : null;
      if (hint && hint !== msg) {
        msg = `${msg} (${hint})`;
      }
      // eslint-disable-next-line no-console
      console.error(`BC API Error ${status}:`, msg);

      // Convert to a real Error so downstream code can read
      // err.message / err.status / err.details reliably.
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) {
        e.message = "Your session has expired. Please sign in again.";
      }
      return Promise.reject(e);
    }
    if (error.request) {
      const e = new Error(
        "Network error reaching Business Central proxy. Is the backend running?"
      );
      e.status = 0;
      e.details = error.message;
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Convenience: GET /companies (cross-company). Caller must clear the
 * company id first with setCompanyId(null).
 */
export async function listCompanies() {
  const prev = currentCompanyId;
  setCompanyId(null);
  try {
    const r = await bcClient.get("/companies");
    return r.data?.value || [];
  } finally {
    setCompanyId(prev);
  }
}

export default bcClient;